import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { EFilingParty, EFilingAttorney, DocumentMetadataItem } from '@lds-efsp/shared-contracts';
import { getDisplayMetadata, getMetadataDisplayName } from '@lds-efsp/shared-contracts';
import { isFeeWaiverInfoTag, partyHasFeeWaiver, FEE_WAIVER_TAG_LABEL } from '../lib/fee-waiver-tag';

/** Allowed values for a `codeList` metadata field, keyed by metadata code
 *  (e.g. `{ MOTION_OSC_DETAIL: [{ code, name }] }`). Sourced from court policy. */
type CodeListOptions = Record<string, { code: string; name: string }[]>;

interface DocumentMetadataFormProps {
  metadata: DocumentMetadataItem[];
  parties: EFilingParty[];
  attorneys: EFilingAttorney[];
  values: Record<string, unknown>;
  onChange: (code: string, value: unknown) => void;
  codeListOptions?: CodeListOptions;
  brand: BrandConfig;
  /**
   * Offer extraction-sourced roster entries in the existing-data selectors
   * (Walter R81/R82). On a case-initiation there is no court roster for an
   * OCR-minted entry to displace or shadow — the wizard roster IS the filing's
   * party list, and Case Participants displays every entry, so the selectors
   * must offer the same set (never "No parties available" while Case
   * Participants is populated). Callers set this from the filing type; it
   * stays false (the R6 exclusion) on subsequent filings, where the court
   * roster is authoritative and extraction-only entries belong on the
   * NEW_PARTY / NEW_ATTORNEY path instead.
   */
  offerExtractionEntries?: boolean;
  /**
   * Replaces the default "No parties/attorneys available…" empty-state copy
   * (Walter R87 / WOL-443): on an existing-case filing with no case attached
   * the offerable roster is empty BY DESIGN — the fix is the case lookup, so
   * the caller points there instead of at manual re-entry.
   */
  emptyRosterHint?: string;
}

// Group metadata items by logical section for cleaner display
interface MetadataGroup {
  label: string;
  items: DocumentMetadataItem[];
}

function groupMetadata(items: DocumentMetadataItem[]): MetadataGroup[] {
  const filing: DocumentMetadataItem[] = [];
  const opposing: DocumentMetadataItem[] = [];
  const caseDetails: DocumentMetadataItem[] = [];
  const additional: DocumentMetadataItem[] = [];

  for (const item of items) {
    switch (item.code) {
      case 'FILING_PARTY':
      case 'NEW_FILING_PARTY':
      case 'NEW_PLAINTIFF':
      case 'FILING_ATTORNEY':
      case 'NEW_ATTORNEY':
      case 'FORMER_ATTORNEY':
        filing.push(item);
        break;
      case 'RESPONDING_PARTY':
      case 'NEW_RESPONDING_PARTY':
      case 'PARTY_SERVED':
      case 'REPRESENTING':
        opposing.push(item);
        break;
      case 'CASE_CATEGORY':
      case 'MOTION_OSC_DETAIL':
      case 'JURISDICTIONAL_AMOUNT':
      case 'SERVICE_DATE':
        caseDetails.push(item);
        break;
      default:
        additional.push(item);
        break;
    }
  }

  const groups: MetadataGroup[] = [];
  if (filing.length > 0) groups.push({ label: 'Filing Parties', items: filing });
  if (opposing.length > 0) groups.push({ label: 'Opposing / Served Parties', items: opposing });
  if (caseDetails.length > 0) groups.push({ label: 'Case Details', items: caseDetails });
  if (additional.length > 0) {
    // SOR-161: the catch-all bucket is normally "Additional Information", but
    // when its ONLY member is the "Other" document code (ADDITIONAL_INFO), the
    // heading reads that code's filer-facing name to match the field label
    // below. Any other combination — two+ items, or a single
    // non-ADDITIONAL_INFO code — keeps the generic label so unrelated catch-all
    // codes aren't mislabelled. SOR-164: the name comes from the shared
    // override table, never an inline literal.
    const label =
      additional.length === 1 && additional[0].code === 'ADDITIONAL_INFO'
        ? getMetadataDisplayName(additional[0])
        : 'Additional Information';
    groups.push({ label, items: additional });
  }
  return groups;
}

const DocumentMetadataForm: React.FC<DocumentMetadataFormProps> = ({
  metadata,
  parties,
  attorneys,
  values,
  onChange,
  codeListOptions,
  brand,
  offerExtractionEntries = false,
  emptyRosterHint,
}) => {
  if (metadata.length === 0) return null;

  const groups = groupMetadata(metadata);

  // R18/R19 (Walter 2026-07-10): this document's filing party is
  // self-represented — by doc-level SELF_REP metadata OR by the party flag.
  // The SAME predicate the envelope (stage-3 `isSelfRep`) and the shared
  // validator use. Drives the "Self-represented (no attorney)" option in the
  // FILING_ATTORNEY control, so a pro per filer isn't dead-ended by
  // "No attorneys available".
  const filingPartyIds = Array.isArray(values.FILING_PARTY)
    ? values.FILING_PARTY.filter((id): id is string => typeof id === 'string')
    : [];
  const filingPartySelfRep =
    values.SELF_REP === true ||
    values.SELF_REP === 'true' ||
    filingPartyIds.some(id => parties.find(p => p.id === id)?.isSelfRepresented === true);

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.label}>
          <p
            className="text-xs font-semibold uppercase mb-1.5"
            style={{ color: brand.colors.textLight }}
          >
            {group.label}
          </p>
          <div className="space-y-2">
            {group.items.map((meta) => (
              <MetadataField
                key={meta.code}
                meta={meta}
                parties={parties}
                attorneys={attorneys}
                value={values[meta.code]}
                flagValues={(values[`${meta.code}_FLAGS`] as Record<string, Record<string, boolean>>) || {}}
                filingPartySelfRep={filingPartySelfRep}
                offerExtractionEntries={offerExtractionEntries}
                emptyRosterHint={emptyRosterHint}
                onChange={onChange}
                codeListOptions={codeListOptions}
                brand={brand}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Individual metadata field renderer
// ---------------------------------------------------------------------------

interface MetadataFieldProps {
  meta: DocumentMetadataItem;
  parties: EFilingParty[];
  attorneys: EFilingAttorney[];
  value: unknown;
  flagValues: Record<string, Record<string, boolean>>;
  /** The document's filing party is self-represented (R18/R19). */
  filingPartySelfRep?: boolean;
  /** Offer extraction-sourced entries (R81/R82 — see DocumentMetadataFormProps). */
  offerExtractionEntries?: boolean;
  /** Empty-roster copy override (R87 — see DocumentMetadataFormProps). */
  emptyRosterHint?: string;
  onChange: (code: string, value: unknown) => void;
  codeListOptions?: CodeListOptions;
  brand: BrandConfig;
}

const MetadataField: React.FC<MetadataFieldProps> = ({
  meta,
  parties,
  attorneys,
  value,
  flagValues,
  filingPartySelfRep,
  offerExtractionEntries,
  emptyRosterHint,
  onChange,
  codeListOptions,
  brand,
}) => {
  switch (meta.classType) {
    case 'caseParticipant':
      if (meta.valueRestriction === 'new-data') {
        return <NewPartyField meta={meta} value={value} onChange={onChange} brand={brand} />;
      }
      return (
        <PartySelector
          meta={meta}
          parties={parties}
          value={value}
          flagValues={flagValues}
          offerExtractionEntries={offerExtractionEntries}
          emptyRosterHint={emptyRosterHint}
          onChange={onChange}
          brand={brand}
        />
      );

    case 'caseAssignment':
      if (meta.valueRestriction === 'new-data') {
        return <NewAttorneyField meta={meta} attorneys={attorneys} value={value} onChange={onChange} brand={brand} />;
      }
      return (
        <AttorneySelector
          meta={meta}
          attorneys={attorneys}
          value={value}
          filingPartySelfRep={filingPartySelfRep}
          offerExtractionEntries={offerExtractionEntries}
          emptyRosterHint={emptyRosterHint}
          onChange={onChange}
          brand={brand}
        />
      );

    case 'contact':
      return <AddressField meta={meta} value={value} onChange={onChange} brand={brand} />;

    case 'codeList':
      return <CodeListField meta={meta} value={value} onChange={onChange} options={codeListOptions?.[meta.code]} brand={brand} />;

    case 'date':
      return <DateField meta={meta} value={value} onChange={onChange} brand={brand} />;

    case 'boolean':
      return <BooleanField meta={meta} value={value} onChange={onChange} brand={brand} />;

    case 'text': {
      // SOR-157 EARS-04: the supplemental field for an "Other" document code
      // (identifier ADDITIONAL_INFO) must read the filer-facing name, not the
      // policy's generic "Additional Info". SOR-164: the override — including
      // SOR-161's `description` clearing, which lets TextField's placeholder
      // fall back to the overridden name instead of the leftover policy string
      // — now lives in the shared table in shared-contracts, so this display
      // surface can't drift from the group heading or the validation message.
      // DISPLAY only: the golden-gated JTI envelope rule (stage-3-envelope.ts)
      // consumes ADDITIONAL_INFO by code and is deliberately untouched.
      const displayMeta = getDisplayMetadata(meta);
      return <TextField meta={displayMeta} value={value} onChange={onChange} brand={brand} />;
    }

    default:
      return (
        <div className="text-xs" style={{ color: brand.colors.textLight }}>
          Unknown field type: {meta.classType} ({meta.name})
        </div>
      );
  }
};

// ---------------------------------------------------------------------------
// Field label helper
// ---------------------------------------------------------------------------

interface FieldLabelProps {
  meta: DocumentMetadataItem;
  brand: BrandConfig;
  hasValue: boolean;
  /** When set, the label associates with that control id (ui/Input.tsx idiom). */
  controlId?: string;
}

const FieldLabel: React.FC<FieldLabelProps> = ({ meta, brand, hasValue, controlId }) => {
  // With a controlId the wrapper is a real <label htmlFor> (the ui/Input.tsx
  // idiom); without one it stays the plain <div> it always was, so the
  // call sites that don't associate a control render unchanged.
  const content = (
    <>
      <span
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: hasValue ? '#10B981' : '#F59E0B' }}
      />
      <span className="text-xs font-medium" style={{ color: brand.colors.text }}>
        {meta.name}
        {/* WOL-463: the court's policy marks this item required — leaving it
            empty is JTI 4082 "Missing Required Metadata Item" at fee calc. */}
        {meta.required && (
          <span aria-label="required by the court" title="Required by the court" style={{ color: '#DC2626' }}>
            {' '}*
          </span>
        )}
      </span>
    </>
  );
  if (!controlId) return <div className="flex items-center gap-1.5 mb-1">{content}</div>;
  return <label htmlFor={controlId} className="flex items-center gap-1.5 mb-1">{content}</label>;
};

// ---------------------------------------------------------------------------
// caseParticipant (existing) — party checkbox list
// ---------------------------------------------------------------------------

interface PartySelectorProps {
  emptyRosterHint?: string;
  meta: DocumentMetadataItem;
  parties: EFilingParty[];
  value: unknown;
  flagValues: Record<string, Record<string, boolean>>;
  /** Offer extraction-sourced entries (R81/R82 — see DocumentMetadataFormProps). */
  offerExtractionEntries?: boolean;
  onChange: (code: string, value: unknown) => void;
  brand: BrandConfig;
}

function filterParties(
  parties: EFilingParty[],
  filter?: string,
  offerExtractionEntries?: boolean,
): EFilingParty[] {
  // R6: on a filing with a court roster (subsequent), existing-data dropdowns
  // must never OFFER an extraction-sourced entry. The court roster stays
  // authoritative, and applyExtraction merges rather than replaces (SOR-223),
  // so an OCR-minted party must not displace or shadow a roster party in the
  // FILING_PARTY / RESPONDING_PARTY / etc. option list. An absent tag is
  // treated as 'manual', so legacy/manual entries are kept — only an explicit
  // 'extraction' source is excluded.
  //
  // R81/R82: on a case-initiation the caller sets `offerExtractionEntries` —
  // there is no court roster to protect, and the extraction-minted entries the
  // Case Participants section displays must be selectable here too.
  const offered = offerExtractionEntries
    ? parties
    : parties.filter(p => p.source !== 'extraction');
  if (!filter) return offered;
  // Common JTI filters
  if (filter.includes('except-attorney')) {
    return offered.filter(p => p.roleCode !== 'ATT');
  }
  return offered;
}

function getPartyLabel(party: EFilingParty): string {
  const name = party.partyType === 'person'
    ? `${party.firstName || ''} ${party.lastName || ''}`
    : party.organizationName || '';
  return `${party.roleName}: ${name.trim()}`;
}

const PartySelector: React.FC<PartySelectorProps> = ({
  meta,
  parties,
  value,
  flagValues,
  offerExtractionEntries,
  emptyRosterHint,
  onChange,
  brand,
}) => {
  const selectedIds = Array.isArray(value) ? (value as string[]) : [];
  const filteredParties = filterParties(parties, meta.filter, offerExtractionEntries);
  const hasTags = meta.additionalInfoTags.length > 0;

  const toggleParty = (partyId: string) => {
    const next = selectedIds.includes(partyId)
      ? selectedIds.filter(id => id !== partyId)
      : [...selectedIds, partyId];
    onChange(meta.code, next);
  };

  const toggleFlag = (partyId: string, tag: string) => {
    const partyFlags = flagValues[partyId] || {};
    const updated = {
      ...flagValues,
      [partyId]: { ...partyFlags, [tag]: !partyFlags[tag] },
    };
    onChange(`${meta.code}_FLAGS`, updated);
  };

  if (filteredParties.length === 0) {
    return (
      <div>
        <FieldLabel meta={meta} brand={brand} hasValue={false} />
        <p className="text-xs pl-3" style={{ color: brand.colors.warning || '#F59E0B' }}>
          {emptyRosterHint ?? 'No parties available. Add parties in the Case Participants section.'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <FieldLabel meta={meta} brand={brand} hasValue={selectedIds.length > 0} />
      <div className="pl-3 space-y-1">
        {filteredParties.map(party => {
          const isSelected = selectedIds.includes(party.id);
          return (
            <div key={party.id}>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleParty(party.id)}
                  style={{ accentColor: brand.colors.primary }}
                />
                <span style={{ color: brand.colors.text }}>{getPartyLabel(party)}</span>
              </label>
              {/* Per-party flags (fee exemption, eService, etc.) */}
              {isSelected && hasTags && (
                <div className="pl-6 flex flex-wrap gap-3 mt-0.5">
                  {meta.additionalInfoTags.map(tag => {
                    // SOR-150 (FWR-2/3/8): the fee-waiver tag is DERIVED from the
                    // party's fee-waiver fields — the single source of truth — and
                    // rendered read-only, never routed through the write-only
                    // `_FLAGS` store that nothing reads. Its label is the one
                    // consistent "Fee Waiver on File" (FWR-8), not the cosmetic
                    // `EFSP_`-stripping derivation below. This keeps the checkbox
                    // from lying: it reflects the party without re-entry (FWR-2)
                    // and cannot diverge from the fee calc / envelope (FWR-3).
                    if (isFeeWaiverInfoTag(tag)) {
                      return (
                        <label
                          key={tag}
                          className="flex items-center gap-1 text-[10px]"
                          title="Set on the party's Fee Exemption Type"
                        >
                          <input
                            type="checkbox"
                            checked={partyHasFeeWaiver(party)}
                            disabled
                            readOnly
                            style={{ accentColor: brand.colors.primary }}
                          />
                          <span style={{ color: brand.colors.textLight }}>
                            {FEE_WAIVER_TAG_LABEL}
                            <span className="opacity-70"> (from party)</span>
                          </span>
                        </label>
                      );
                    }
                    const label = tag
                      .replace('EFSP_', '')
                      .replace(/_/g, ' ')
                      .toLowerCase()
                      .replace(/\b\w/g, c => c.toUpperCase());
                    return (
                      <label key={tag} className="flex items-center gap-1 text-[10px] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={flagValues[party.id]?.[tag] || false}
                          onChange={() => toggleFlag(party.id, tag)}
                          style={{ accentColor: brand.colors.primary }}
                        />
                        <span style={{ color: brand.colors.textLight }}>{label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// caseParticipant (new) — inline party creation
// ---------------------------------------------------------------------------

interface NewPartyFieldProps {
  meta: DocumentMetadataItem;
  value: unknown;
  onChange: (code: string, value: unknown) => void;
  brand: BrandConfig;
}

const NewPartyField: React.FC<NewPartyFieldProps> = ({ meta, value, onChange, brand }) => {
  const data = (value || {}) as Record<string, string>;
  const hasValue = !!(data.firstName || data.lastName || data.organizationName);

  const update = (field: string, val: string) => {
    onChange(meta.code, { ...data, [field]: val });
  };

  return (
    <div>
      <FieldLabel meta={meta} brand={brand} hasValue={hasValue} />
      <div className="pl-3 grid grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="First name"
          aria-label="First name"
          value={data.firstName || ''}
          onChange={e => update('firstName', e.target.value)}
          className="px-2 py-1 border rounded text-xs"
          style={{ borderColor: brand.colors.border, color: brand.colors.text }}
        />
        <input
          type="text"
          placeholder="Last name"
          aria-label="Last name"
          value={data.lastName || ''}
          onChange={e => update('lastName', e.target.value)}
          className="px-2 py-1 border rounded text-xs"
          style={{ borderColor: brand.colors.border, color: brand.colors.text }}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// caseAssignment (existing) — attorney dropdown
// ---------------------------------------------------------------------------

interface AttorneySelectorProps {
  emptyRosterHint?: string;
  meta: DocumentMetadataItem;
  attorneys: EFilingAttorney[];
  value: unknown;
  /** The document's filing party is self-represented (R18/R19). */
  filingPartySelfRep?: boolean;
  /** Offer extraction-sourced entries (R81/R82 — see DocumentMetadataFormProps). */
  offerExtractionEntries?: boolean;
  onChange: (code: string, value: unknown) => void;
  brand: BrandConfig;
}

const AttorneySelector: React.FC<AttorneySelectorProps> = ({
  meta,
  attorneys,
  value,
  filingPartySelfRep,
  offerExtractionEntries,
  emptyRosterHint,
  onChange,
  brand,
}) => {
  // WOL-344 fix: mv.FORMER_ATTORNEY (and mv.FILING_ATTORNEY) are now stored as
  // string[] so getPartyIds (stage-3-envelope.ts:585) can unwrap them.  Support
  // both the new array form and any legacy bare-string values already persisted.
  const selectedId = Array.isArray(value)
    ? (value[0] ?? '')
    : (typeof value === 'string' ? value : '');

  // R6: on a subsequent filing, existing-data dropdowns must never OFFER an
  // extraction-sourced entry (see filterParties) — an OCR-minted attorney must
  // not displace or shadow a roster attorney in the FILING_ATTORNEY /
  // FORMER_ATTORNEY option list. Filter both the empty-state check and the
  // option .map so an all-extraction roster shows "No attorneys available"
  // rather than an empty <select>. R81/R82: on a case-initiation the caller
  // sets `offerExtractionEntries` and the extraction-minted attorneys the Case
  // Participants section displays are selectable here too.
  const offeredAttorneys = offerExtractionEntries
    ? attorneys
    : attorneys.filter(a => a.source !== 'extraction');

  // R19 (Walter 2026-07-10): a self-rep filing party satisfies FILING_ATTORNEY
  // with no attorney — offer that as an explicit selection instead of the
  // "No attorneys available" dead end. FILING_ATTORNEY only: a substitution's
  // FORMER_ATTORNEY genuinely requires an attorney regardless.
  const offerSelfRep = meta.code === 'FILING_ATTORNEY' && !!filingPartySelfRep;

  // Both production mounts render one form per document, so ids are minted per
  // field instance — a literal id would collide across same-typed documents.
  const controlId = React.useId();

  if (offeredAttorneys.length === 0 && !offerSelfRep) {
    return (
      <div>
        <FieldLabel meta={meta} brand={brand} hasValue={false} />
        <p className="text-xs pl-3" style={{ color: brand.colors.warning || '#F59E0B' }}>
          {emptyRosterHint ?? 'No attorneys available. Add attorneys in the Case Participants section.'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <FieldLabel meta={meta} brand={brand} hasValue={!!selectedId || offerSelfRep} controlId={controlId} />
      <select
        id={controlId}
        value={selectedId}
        // WOL-344 fix: emit string[] so getPartyIds can unwrap; empty selection → [].
        // R19: the self-rep option IS the empty selection — the envelope derives
        // self-rep from the party flag / SELF_REP metadata and emits the
        // FILING_PARTY_ADDRESS block; FILING_ATTORNEY stays [], never a sentinel.
        onChange={e => onChange(meta.code, e.target.value ? [e.target.value] : [])}
        className="ml-3 px-2 py-1 border rounded text-xs"
        style={{ borderColor: brand.colors.border, color: brand.colors.text }}
      >
        <option value="">
          {offerSelfRep ? 'Self-represented (no attorney)' : 'Select attorney...'}
        </option>
        {offeredAttorneys.map(atty => (
          <option key={atty.id} value={atty.id}>
            {atty.firstName} {atty.lastName}{atty.barNumber ? ` (Bar #${atty.barNumber})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
};

// ---------------------------------------------------------------------------
// caseAssignment (new) — attorney creation form
// ---------------------------------------------------------------------------

interface NewAttorneyFieldProps {
  meta: DocumentMetadataItem;
  attorneys: EFilingAttorney[];
  value: unknown;
  onChange: (code: string, value: unknown) => void;
  brand: BrandConfig;
}

// The captured NEW_ATTORNEY payload mirrors the EFilingAttorney shape so the
// JTI envelope builder (`buildNewAttorneyMetadataItem`) can emit its contact
// block directly. `address` is a nested object keyed like SelfRepAddressSchema.
interface CapturedNewAttorney {
  barNumber?: string;
  firstName?: string;
  lastName?: string;
  firmName?: string;
  email?: string;
  phone?: string;
  address?: { country?: string; address1?: string; city?: string; state?: string; zip?: string };
}

const NewAttorneyField: React.FC<NewAttorneyFieldProps> = ({ meta, attorneys, value, onChange, brand }) => {
  // SOR-210: NEW_ATTORNEY persists as a JSON array of attorney objects (the
  // sibling-array convention; mirrors AttorneySelector's WOL-344 string[]).
  // Normalize-on-read tolerates a legacy single-object value: read the first
  // array element when an array, else the bare object. The next edit always
  // re-persists as an array, so a legacy value upgrades on first interaction.
  const data = (Array.isArray(value) ? (value[0] ?? {}) : (value || {})) as CapturedNewAttorney;
  const addr = data.address || {};
  const hasValue = !!(data.firstName || data.lastName || data.barNumber);
  // Cert (Brenda, 26MA00005150): a Substitution must carry the incoming
  // attorney's contact info or JTI's CMS flags "missing new atty contact info".
  // The builder renders ContactInformation only when these are present, so the
  // capture form must collect them.
  const hasContact = !!(data.email || addr.address1);

  const update = (field: keyof CapturedNewAttorney, val: string) => {
    onChange(meta.code, [{ ...data, [field]: val }]);
  };
  const updateAddress = (field: string, val: string) => {
    onChange(meta.code, [{ ...data, address: { country: 'US', ...addr, [field]: val } }]);
  };

  // Pre-fill: if the entered bar number matches a roster attorney that already
  // carries contact info (e.g. one added via AddAttorneyModal with role
  // 'substituting'), offer one-click fill to avoid re-typing.
  const norm = (s?: string) => (s || '').replace(/\s+/g, '').toLowerCase();
  const rosterMatch = data.barNumber
    ? attorneys.find(a => norm(a.barNumber) === norm(data.barNumber) && (a.email || a.address?.address1))
    : undefined;
  const fillFromRoster = () => {
    if (!rosterMatch) return;
    onChange(meta.code, [{
      ...data,
      firstName: data.firstName || rosterMatch.firstName,
      lastName: data.lastName || rosterMatch.lastName,
      firmName: data.firmName || rosterMatch.firmName,
      email: data.email || rosterMatch.email,
      phone: data.phone || rosterMatch.phone,
      address: data.address?.address1 ? data.address : rosterMatch.address,
    }]);
  };

  const inputCls = 'px-2 py-1 border rounded text-xs';
  const inputStyle = { borderColor: brand.colors.border, color: brand.colors.text };

  return (
    <div>
      <FieldLabel meta={meta} brand={brand} hasValue={hasValue} />
      <div className="pl-3 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <input
            type="text"
            placeholder="Bar number"
            aria-label="Bar number"
            value={data.barNumber || ''}
            onChange={e => update('barNumber', e.target.value)}
            className={inputCls}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="First name"
            aria-label="First name"
            value={data.firstName || ''}
            onChange={e => update('firstName', e.target.value)}
            className={inputCls}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="Last name"
            aria-label="Last name"
            value={data.lastName || ''}
            onChange={e => update('lastName', e.target.value)}
            className={inputCls}
            style={inputStyle}
          />
        </div>
        {rosterMatch && !hasContact && (
          <button
            type="button"
            onClick={fillFromRoster}
            className="text-xs underline"
            style={{ color: brand.colors.link }}
          >
            Fill contact details from {rosterMatch.firstName} {rosterMatch.lastName}
          </button>
        )}
        <input
          type="text"
          placeholder="Firm name (optional)"
          aria-label="Firm name"
          value={data.firmName || ''}
          onChange={e => update('firmName', e.target.value)}
          className={`w-full ${inputCls}`}
          style={inputStyle}
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            type="email"
            placeholder="Email"
            aria-label="Email"
            value={data.email || ''}
            onChange={e => update('email', e.target.value)}
            className={inputCls}
            style={inputStyle}
          />
          <input
            type="tel"
            placeholder="Phone"
            aria-label="Phone"
            value={data.phone || ''}
            onChange={e => update('phone', e.target.value)}
            className={inputCls}
            style={inputStyle}
          />
        </div>
        <input
          type="text"
          placeholder="Street address"
          aria-label="Street address"
          value={addr.address1 || ''}
          onChange={e => updateAddress('address1', e.target.value)}
          className={`w-full ${inputCls}`}
          style={inputStyle}
        />
        <div className="grid grid-cols-3 gap-2">
          <input
            type="text"
            placeholder="City"
            aria-label="City"
            value={addr.city || ''}
            onChange={e => updateAddress('city', e.target.value)}
            className={inputCls}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="State"
            aria-label="State"
            value={addr.state || ''}
            onChange={e => updateAddress('state', e.target.value)}
            className={inputCls}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="ZIP"
            aria-label="ZIP"
            value={addr.zip || ''}
            onChange={e => updateAddress('zip', e.target.value)}
            className={inputCls}
            style={inputStyle}
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// contact — address form
// ---------------------------------------------------------------------------

interface AddressFieldProps {
  meta: DocumentMetadataItem;
  value: unknown;
  onChange: (code: string, value: unknown) => void;
  brand: BrandConfig;
}

const AddressField: React.FC<AddressFieldProps> = ({ meta, value, onChange, brand }) => {
  const data = (value || {}) as Record<string, string>;
  const hasValue = !!(data.address1 || data.city);

  const update = (field: string, val: string) => {
    onChange(meta.code, { ...data, [field]: val });
  };

  return (
    <div>
      <FieldLabel meta={meta} brand={brand} hasValue={hasValue} />
      <div className="pl-3 space-y-1">
        <input
          type="text"
          placeholder="Street address"
          aria-label="Street address"
          value={data.address1 || ''}
          onChange={e => update('address1', e.target.value)}
          className="w-full px-2 py-1 border rounded text-xs"
          style={{ borderColor: brand.colors.border, color: brand.colors.text }}
        />
        <div className="grid grid-cols-3 gap-2">
          <input
            type="text"
            placeholder="City"
            aria-label="City"
            value={data.city || ''}
            onChange={e => update('city', e.target.value)}
            className="px-2 py-1 border rounded text-xs"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          />
          <input
            type="text"
            placeholder="State"
            aria-label="State"
            value={data.state || ''}
            onChange={e => update('state', e.target.value)}
            className="px-2 py-1 border rounded text-xs"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          />
          <input
            type="text"
            placeholder="ZIP"
            aria-label="ZIP"
            value={data.zip || ''}
            onChange={e => update('zip', e.target.value)}
            className="px-2 py-1 border rounded text-xs"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// codeList — dropdown (values from court policy)
// ---------------------------------------------------------------------------

interface CodeListFieldProps {
  meta: DocumentMetadataItem;
  value: unknown;
  onChange: (code: string, value: unknown) => void;
  options?: { code: string; name: string }[];
  brand: BrandConfig;
}

const CodeListField: React.FC<CodeListFieldProps> = ({ meta, value, onChange, options, brand }) => {
  const selectedCode = typeof value === 'string' ? value : '';
  // Both production mounts render one form per document, so ids are minted per
  // field instance — a literal id would collide across same-typed documents.
  const controlId = React.useId();

  // codeList fields (e.g. MOTION_OSC_DETAIL) expect a court-policy code, not free
  // text — JTI rejects free text with [4061] Invalid MetaData Code value. When the
  // policy-sourced options are available, render a dropdown that persists the code;
  // fall back to a text input only when no options loaded (so unmapped code lists
  // don't hard-block).
  if (options && options.length > 0) {
    // Preserve a previously-saved value even if it's no longer in the option set.
    const hasSelected = !selectedCode || options.some(o => o.code === selectedCode);
    return (
      <div>
        <FieldLabel meta={meta} brand={brand} hasValue={!!selectedCode} controlId={controlId} />
        <select
          id={controlId}
          value={selectedCode}
          onChange={e => onChange(meta.code, e.target.value)}
          className="ml-3 px-2 py-1 border rounded text-xs"
          style={{ borderColor: brand.colors.border, color: brand.colors.text }}
        >
          <option value="">Select {meta.name}...</option>
          {!hasSelected && <option value={selectedCode}>{selectedCode}</option>}
          {options.map(o => (
            <option key={o.code} value={o.code}>{o.name}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div>
      <FieldLabel meta={meta} brand={brand} hasValue={!!selectedCode} controlId={controlId} />
      <input
        type="text"
        id={controlId}
        placeholder={`Enter ${meta.name}...`}
        value={selectedCode}
        onChange={e => onChange(meta.code, e.target.value)}
        className="ml-3 px-2 py-1 border rounded text-xs"
        style={{ borderColor: brand.colors.border, color: brand.colors.text }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// date — date picker
// ---------------------------------------------------------------------------

interface DateFieldProps {
  meta: DocumentMetadataItem;
  value: unknown;
  onChange: (code: string, value: unknown) => void;
  brand: BrandConfig;
}

const DateField: React.FC<DateFieldProps> = ({ meta, value, onChange, brand }) => {
  const dateValue = typeof value === 'string' ? value : '';
  // Both production mounts render one form per document, so ids are minted per
  // field instance — a literal id would collide across same-typed documents.
  const controlId = React.useId();

  return (
    <div>
      <FieldLabel meta={meta} brand={brand} hasValue={!!dateValue} controlId={controlId} />
      <input
        type="date"
        id={controlId}
        value={dateValue}
        onChange={e => onChange(meta.code, e.target.value)}
        className="ml-3 px-2 py-1 border rounded text-xs"
        style={{ borderColor: brand.colors.border, color: brand.colors.text }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// boolean — checkbox
// ---------------------------------------------------------------------------

interface BooleanFieldProps {
  meta: DocumentMetadataItem;
  value: unknown;
  onChange: (code: string, value: unknown) => void;
  brand: BrandConfig;
}

const BooleanField: React.FC<BooleanFieldProps> = ({ meta, value, onChange, brand }) => {
  const checked = value === true;

  return (
    <div>
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <span
          className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: typeof value === 'boolean' ? '#10B981' : '#F59E0B' }}
        />
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(meta.code, e.target.checked)}
          style={{ accentColor: brand.colors.primary }}
        />
        <span style={{ color: brand.colors.text }}>{meta.name}</span>
      </label>
    </div>
  );
};

// ---------------------------------------------------------------------------
// text — text input
// ---------------------------------------------------------------------------

interface TextFieldProps {
  meta: DocumentMetadataItem;
  value: unknown;
  onChange: (code: string, value: unknown) => void;
  brand: BrandConfig;
}

const TextField: React.FC<TextFieldProps> = ({ meta, value, onChange, brand }) => {
  const textValue = typeof value === 'string' ? value : '';
  // Both production mounts render one form per document, so ids are minted per
  // field instance — a literal id would collide across same-typed documents.
  const controlId = React.useId();

  return (
    <div>
      <FieldLabel meta={meta} brand={brand} hasValue={!!textValue} controlId={controlId} />
      <textarea
        id={controlId}
        placeholder={meta.description || `Enter ${meta.name}...`}
        value={textValue}
        onChange={e => onChange(meta.code, e.target.value)}
        rows={2}
        className="ml-3 w-[calc(100%-0.75rem)] px-2 py-1 border rounded text-xs resize-y"
        style={{ borderColor: brand.colors.border, color: brand.colors.text }}
      />
    </div>
  );
};

export default DocumentMetadataForm;
